// src/api/axiosInstance.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth';
import { clearPersistedState } from '../stores/utils';
import { refreshTokenApi } from './authApi'; // Import the new refresh token API
import { jwtDecode } from 'jwt-decode'; // Import jwt-decode
import toast from 'react-hot-toast'; // Import react-hot-toast

const API_BASE_URL = import.meta.env.VITE_API_URL;

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Attach token and log requests
axiosInstance.interceptors.request.use(
  (config) => {
    const authStorage = localStorage.getItem('auth-storage');
    const parsedAuthStorage = authStorage ? JSON.parse(authStorage) : null;
    const token = parsedAuthStorage?.state?.token || null;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`[AUTH] Attaching token to request to ${config.url}`);
    } else {
      console.log(`[AUTH] No token found for request to ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle global errors, especially 401 Unauthorized
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const authStore = useAuthStore.getState();
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Centralized Error Notification
    const errorMessage = (error.response?.data as { message?: string })?.message || error.message || 'An unexpected error occurred.';
    if (error.response?.status !== 401) { // Avoid showing toast for 401, as it's handled by redirect
      toast.error(`API Error: ${errorMessage}`);
    }

    // Check if guest info exists, skip logout/redirection if yes
    const isGuest = !!localStorage.getItem('guest-info');

    // Token Refresh Mechanism
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.warn('[AUTH] 401 Unauthorized error detected. Attempting token refresh.');

      const currentRefreshToken = authStore.refreshToken;

      if (currentRefreshToken) {
        try {
          const refreshResponse = await refreshTokenApi(currentRefreshToken);
          const newAccessToken = refreshResponse.accessToken;

          authStore.setToken(newAccessToken, currentRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          console.log('[AUTH] Token refreshed, retrying original request.');
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('[AUTH] Token refresh failed:', refreshError);
          // Fall through to logout if refresh fails
        }
      }

      // If no refresh token or refresh failed, log out and redirect — skip if guest
      if (!isGuest) {
        console.log('[AUTH] Refresh token not available or refresh failed. Clearing auth and redirecting.');
        authStore.logout();
        clearPersistedState('auth-storage');
        window.location.href = '/';
      } else {
        console.warn('[GUEST] Guest detected — skipping logout/redirect.');
      }
    }

    // Client-side Token Expiration Check (Proactive Logout)
    const currentAccessToken = authStore.token;
    if (currentAccessToken) {
      try {
        const decodedToken: { exp: number } = jwtDecode(currentAccessToken);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          if (!isGuest) {
            console.warn('[AUTH] Client-side detected expired token. Logging out proactively.');
            authStore.logout();
            clearPersistedState('auth-storage');
            window.location.href = '/';
          } else {
            console.warn('[GUEST] Guest detected — skipping proactive logout.');
          }
        }
      } catch (decodeError) {
        if (!isGuest) {
          console.error('[AUTH] Error decoding token:', decodeError);
          authStore.logout();
          clearPersistedState('auth-storage');
          window.location.href = '/';
        } else {
          console.warn('[GUEST] Guest detected — skipping token decode error handling.');
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
